import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Entity } from '@backstage/catalog-model';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import {
  EntityRefLink,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';

/**
 * The GCP resources a component depends on, grouped by the environment each
 * resource reports.
 *
 * The built-in EntityDependsOnResourcesCard renders one flat table, which for a
 * component whose `gcp.com/application` claim spans environments means its live
 * and staging databases sit side by side with nothing to tell them apart. The
 * distinction is already in the data: the gcp-asset-ingestor sets
 * `spec.environment` on every Resource from its GCP `environment` label, falling
 * back to the environment configured for its project. So this groups the same
 * relations rather than introducing an entity per environment.
 */

// Rendered in this order; anything else is appended under its own heading.
const ENVIRONMENT_ORDER = ['live', 'staging', 'dev'];

const ENVIRONMENT_COLOR: Record<
  string,
  'error' | 'warning' | 'info' | 'default'
> = {
  live: 'error',
  staging: 'warning',
  dev: 'info',
};

// Coarse spec.type -> display label, so the rows read as infrastructure rather
// than as ingestor jargon.
const TYPE_LABEL: Record<string, string> = {
  database: 'Cloud SQL',
  cache: 'Memorystore',
  storage: 'Bucket',
  'kubernetes-cluster': 'GKE',
  'load-balancer': 'Load balancer',
  'load-balancer-frontend': 'LB frontend',
  'dns-zone': 'DNS zone',
  'dns-record': 'DNS record',
  'vpc-network': 'VPC',
  subnet: 'Subnet',
  'cloud-router': 'Cloud Router',
};

// digistore claims 39 DNS zones and 39 buckets per environment, which would
// bury the 3 databases and 6 caches that anyone actually looks for. Above this
// many of one type, collapse to a single expandable row.
const COLLAPSE_ABOVE = 5;

const CONSOLE = 'https://console.cloud.google.com';

function consoleUrl(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const project = annotations['gcp.com/project'];
  if (!project) return undefined;
  const name = entity.metadata.name;
  const q = `project=${project}`;
  switch (entity.spec?.type) {
    case 'database':
      return `${CONSOLE}/sql/instances/${name}/overview?${q}`;
    case 'cache':
      return `${CONSOLE}/memorystore/redis/instances?${q}`;
    case 'storage':
      return `${CONSOLE}/storage/browser/${name}?${q}`;
    case 'kubernetes-cluster':
      return `${CONSOLE}/kubernetes/list/overview?${q}`;
    default:
      return `${CONSOLE}/home/dashboard?${q}`;
  }
}

function environmentOf(entity: Entity): string {
  const env = entity.spec?.environment;
  return typeof env === 'string' && env ? env : 'unknown';
}

function sortEnvironments(environments: string[]): string[] {
  return environments.sort((a, b) => {
    const ia = ENVIRONMENT_ORDER.indexOf(a);
    const ib = ENVIRONMENT_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

/** Cluster resources sharing an environment, so the group can name where it runs. */
function clustersFor(resources: Entity[], environment: string): string[] {
  return resources
    .filter(
      r =>
        r.spec?.type === 'kubernetes-cluster' &&
        environmentOf(r) === environment,
    )
    .map(r => r.metadata.name);
}

const ResourceRow = ({ resource }: { resource: Entity }) => {
  const annotations = resource.metadata.annotations ?? {};
  const type = String(resource.spec?.type ?? '');
  const url = consoleUrl(resource);
  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell sx={{ py: 0.5, pl: 0, width: 130 }}>
        <Typography variant="caption" color="text.secondary">
          {TYPE_LABEL[type] ?? type}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <EntityRefLink entityRef={resource} defaultKind="Resource" />
      </TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {annotations['gcp.com/project']}
          {annotations['gcp.com/location']
            ? ` · ${annotations['gcp.com/location']}`
            : ''}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 0.5, pr: 0, width: 70, textAlign: 'right' }}>
        {url && (
          <Link to={url} title="Open in Cloud Console">
            <Typography variant="caption">Console</Typography>
          </Link>
        )}
      </TableCell>
    </TableRow>
  );
};

/** One row per resource, but collapsed to a summary when a type is numerous. */
const TypeGroup = ({
  type,
  resources,
  groupKey,
  expanded,
  onToggle,
}: {
  type: string;
  resources: Entity[];
  groupKey: string;
  expanded: boolean;
  onToggle: (key: string) => void;
}) => {
  const label = TYPE_LABEL[type] ?? type;
  if (resources.length <= COLLAPSE_ABOVE || expanded) {
    return (
      <>
        {resources.length > COLLAPSE_ABOVE && (
          <TableRow>
            <TableCell colSpan={4} sx={{ py: 0.5, pl: 0, border: 0 }}>
              <MuiLink
                component="button"
                variant="caption"
                onClick={() => onToggle(groupKey)}
              >
                hide {resources.length} {label.toLowerCase()}s
              </MuiLink>
            </TableCell>
          </TableRow>
        )}
        {resources.map(resource => (
          <ResourceRow
            key={`${resource.metadata.namespace}/${resource.metadata.name}`}
            resource={resource}
          />
        ))}
      </>
    );
  }
  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell sx={{ py: 0.5, pl: 0, width: 130 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </TableCell>
      <TableCell colSpan={3} sx={{ py: 0.5 }}>
        <MuiLink
          component="button"
          variant="body2"
          onClick={() => onToggle(groupKey)}
        >
          {resources.length} {label.toLowerCase()}s
        </MuiLink>
      </TableCell>
    </TableRow>
  );
};

export const InfrastructureByEnvironmentCard = () => {
  const { entity } = useEntity();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: 'dependsOn',
    kind: 'Resource',
  });

  if (loading) return <Progress />;
  if (error || !entities || entities.length === 0) return null;

  // Only the ingestor's resources carry spec.environment; anything else would
  // land in a meaningless "unknown" bucket, so leave those to the built-in card.
  const resources = entities.filter(
    e => (e.metadata.annotations ?? {})['gcp.com/project'],
  );
  if (resources.length === 0) return null;

  const byEnvironment = new Map<string, Entity[]>();
  for (const resource of resources) {
    const env = environmentOf(resource);
    byEnvironment.set(env, [...(byEnvironment.get(env) ?? []), resource]);
  }
  const environments = sortEnvironments([...byEnvironment.keys()]);

  return (
    <InfoCard
      title="Infrastructure by environment"
      subheader={`${resources.length} GCP resources across ${environments.length} environment${
        environments.length === 1 ? '' : 's'
      }`}
    >
      {environments.map(env => {
        const group = byEnvironment.get(env) ?? [];
        const byType = new Map<string, Entity[]>();
        for (const resource of group) {
          const type = String(resource.spec?.type ?? '');
          byType.set(type, [...(byType.get(type) ?? []), resource]);
        }
        // fewest-first, so the handful of databases and caches read before the
        // collapsed rows for buckets and DNS zones
        const types = [...byType.keys()].sort(
          (a, b) =>
            (byType.get(a)?.length ?? 0) - (byType.get(b)?.length ?? 0) ||
            a.localeCompare(b),
        );
        const clusters = clustersFor(resources, env);
        return (
          <Box key={env} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
              <Chip
                label={env}
                size="small"
                color={ENVIRONMENT_COLOR[env] ?? 'default'}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                {group.length} resource{group.length === 1 ? '' : 's'}
                {clusters.length > 0 ? ` · ${clusters.join(', ')}` : ''}
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {types.map(type => (
                  <TypeGroup
                    key={type}
                    type={type}
                    groupKey={`${env}/${type}`}
                    expanded={expanded.has(`${env}/${type}`)}
                    onToggle={toggle}
                    resources={(byType.get(type) ?? [])
                      .slice()
                      .sort((a, b) =>
                        a.metadata.name.localeCompare(b.metadata.name),
                      )}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })}
    </InfoCard>
  );
};
